import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { find } from 'lodash';
import { IText, translate } from '../../utils/translator';
import { IAccountGroupAM } from '../accounts/account-api';
import {
	IFinancialEntryAccountAM,
	IFinancialEntryAccountGroupAM,
	IFinancialEntryAM,
	IFinancialEntryDataAM,
	IFinancialEntryDimensionValueAM,
	IFinancialEntrySourceAccountingRuleAM
} from './financial-entry-api';

export class FinancialEntryList {
	@bindable financialEntryData: IFinancialEntryDataAM;
	@bindable fiscalYearId: string;
	@bindable scopeId: string;

	@bindable accountGroupListContainer: HTMLElement;

	@bindable summaryGroupList: FinancialEntryListItem[] = [];
	@bindable accountGroupList: AccountGroupListItem[] = [];

	summaryVisible = false;

	attached() {
		this.refreshAccountGroups();
	}

	refreshAccountGroups() {
		this.summaryGroupList = this.mapSummaryGroups(this.financialEntryData.summaryGroups);
		this.accountGroupList = this.mapAccountGroups(this.financialEntryData.accountGroups);
	}

	mapSummaryGroups(summaryGroups: IFinancialEntryAM[]) {
		const calculateIndexInGroup = this.getIndexInGroupCalculator();

		return summaryGroups?.map(entry => new FinancialEntryListItem({
			indexInGroup: calculateIndexInGroup(entry),
			amount: entry.amount,
			source: entry.source,
			type: entry.type,
			created: entry.created,
			isCounterEntry: entry.isCounterEntry,
			serviceName: entry.serviceName,
			municipalityName: entry.municipalityName,
			sourceAccountingRule: entry.sourceAccountingRule != null
				? new SourceAccountingRuleListItem(entry.sourceAccountingRule)
				: null
		}));
	}

	mapAccountGroups(accountGroups: IFinancialEntryAccountGroupAM[]) {
		return accountGroups?.map(accountGroup => new AccountGroupListItem({
			id: accountGroup.id,
			nameText: accountGroup.nameText,
			accounts: accountGroup.accounts.map(account => new AccountListItem({
				id: account.id,
				accountNumber: account.accountNumber,
				name: account.name,
				dimensionValues: account.dimensionValues.map(
					dimensionValue => {
						const calculateIndexInGroup = this.getIndexInGroupCalculator();

						return new DimensionValueListItem({
							title: dimensionValue.title,
							financialEntries: dimensionValue.financialEntries.map(
								entry => new FinancialEntryListItem({
									indexInGroup: calculateIndexInGroup(entry),
									amount: entry.amount,
									source: entry.source,
									type: entry.type,
									created: entry.created,
									isCounterEntry: entry.isCounterEntry,
									serviceName: entry.serviceName,
									municipalityName: entry.municipalityName,
									sourceAccountingRule: entry.sourceAccountingRule != null
										? new SourceAccountingRuleListItem(entry.sourceAccountingRule)
										: null
								})
							)
						});
					}
				),
				amount: account.total
			})),
			amount: accountGroup.total
		}));
	}

	getIndexInGroupCalculator() {
		let previousSource: string = null;
		let index: number = 0;
		return (entry: IFinancialEntryAM) => {
			const source = entry.sourceAccountingRule != null
				? entry.sourceAccountingRule.id
				: entry.source;

			[previousSource, index] = previousSource !== source
				? [source, 0]
				: [previousSource, index + 1];

			return index;
		};
	}

	toggleAccountListVisible(accountGroup: AccountGroupListItem) {
		accountGroup.accountListVisible = !accountGroup.accountListVisible;
	}

	accountGroupsChanged(newValue: string, oldValue: string) {
		this.refreshAccountGroups();
	}
}

export class AccountGroupListItem {
	id: string;
	nameText: string;
	amount: number;
	accounts: AccountListItem[] = [];
	accountListVisible = false;

	constructor(init?: Partial<AccountGroupListItem>) {
		Object.assign(this, init);
	}
}

class AccountListItem {
	id: string;
	accountNumber: string;
	name: string;
	amount: number;
	dimensionValues: DimensionValueListItem[] = [];

	constructor(init?: Partial<AccountListItem>) {
		Object.assign(this, init);
	}
}

class DimensionValueListItem {
	title: string;
	financialEntries: FinancialEntryListItem[];

	constructor(init?: Partial<DimensionValueListItem>) {
		Object.assign(this, init);
	}
}

export class FinancialEntryListItem {
	amount: number;
	source: string;
	type: string;
	created: string;
	isCounterEntry: boolean;
	serviceName: string;
	municipalityName: string;
	sourceAccountingRule: SourceAccountingRuleListItem;
	indexInGroup: number;

	@computedFrom('indexInGroup')
	get first() {
		return this.indexInGroup === 0;
	}

	@computedFrom('source')
	get calculated() {
		return !(this.source === 'Original' || this.source === 'ManualTransfer');
	}

	constructor(init?: Partial<FinancialEntryListItem>) {
		Object.assign(this, init);
	}
}

class SourceAccountingRuleListItem {
	id: string;
	name: string;
	ruleIndex: number;
	folderIndex: number;
	definition: string;
	isAllowedAccessAccountingRules: boolean;

	constructor(init?: Partial<SourceAccountingRuleListItem>) {
		Object.assign(this, init);
	}
}
